import React from 'react';
import { observable, action } from 'mobx';
import sortBy from 'lodash/sortBy';

import { defineMessages } from 'react-intl';
import computed from 'lib/mobx/computed';
import JSONSerializable from 'lib/serialization/json-serializable';
import { ApiItem } from 'lib/api/api-item';
import { JSONOptions } from 'lib/api/api-store';
import { ApiStore } from 'lib/api/django-restframework/api-store';
import PAGED_TYPE from 'lib/api/django-restframework/paged-type';
import { PagedResult } from 'lib/api/django-restframework/paged-result/paged-result';
import { ComplaintStore } from 'four-nets/admin/admin';
import SlugLoadableApiStore from 'four-nets/api/slug-loadable-api-store';
import { ROLE } from 'four-nets/user/role';
import { UserStore } from 'four-nets/user/user';
import { LikeStore } from 'four-nets/like/like';
import Likeable from 'four-nets/like/likeable';
import { PhotoFileStore } from 'four-nets/photofile/photofile';
import { DeletedContent } from 'four-nets/common/deleted-content';
import { PurgedContent } from 'four-nets/common/purged-content';

export { Message };

@Likeable()
@JSONSerializable()
class Message extends ApiItem {
    thread: Thread;
    userStore: UserStore;

    // intial timeout for user edits
    EDIT_TIME = 300;

    constructor(options) {
        super(options);

        const { thread, userStore, intl } = options;
        this.thread = thread;
        this.userStore = userStore;
        this.intl = intl;
    }

    fromJSON({ json }) {
        const { thread, user: userJSON, time, photoFiles: photoFilesJSON, ...rest } = json;

        super.fromJSON({
            json: rest,
        });

        this.user = this.userStore.processJSON({ json: userJSON });
        this.time = new Date(time);

        this.photoFiles = this.thread.photoFileStore.processJSONArray({
            json: photoFilesJSON,
            paged: false,
        });
    }

    toJSON() {
        const { thread, ...rest } = super.toJSON();
        return {
            ...rest,
            thread: thread.id,
        };
    }

    complain({ message } = {}) {
        return this.delegate.complain({ message: this, comment: message });
    }

    @computed get displayedContent() {
        if (!this.deleteOn && !this.isPurged) {
            return this.body;
        }
        if (this.isPurged) {
            return new PurgedContent(this.intl);
        }
        return new DeletedContent(this.deleteReason, this.intl);
    }

    updateContent(body) {
        return this.delegate.updateContent(this, body);
    }

    delete(ruleId, comment) {
        return this.delegate.delete(this, ruleId, comment);
    }

    @action recover() {
        this.deleteOn = null;
        this.isPurged = null;
    }

    deleteThread() {
        return this.delegate.deleteThread(this);
    }

    /**
     * Returns timeout for which the message can be edited by currently logged in user.
     *
     * If the user is staff, returns -1, which means editable indefinitely.
     * For normal users, the intial timeout is specified by EDIT_TIME constant.
     * Returning 0 means not editable.
     */
    @computed get editTimeout() {
        if (this.userStore.me) {
            if (this.userStore.me.isSuperstaff) {
                return -1;
            }
            if (this.userStore.me === this.user) {
                const currentTime = new Date();
                const timeDiff = Math.floor((currentTime - this.time) / 1000);
                if (timeDiff < this.EDIT_TIME) {
                    return this.EDIT_TIME - timeDiff;
                }
            }
        }
        return 0;
    }

    @computed get userRole() {
        if (!this.user || !this.thread) {
            return ROLE.none;
        }

        if (this.thread.firstMessage.user === this.user) {
            return ROLE.author;
        }

        if (this.user.vendor) {
            return ROLE.vendor;
        }

        return ROLE.none;
    }

    mailSubject({ intl }) {
        const forum = intl.formatMessage(
            defineMessages({ FORUM_LINK_LABEL: { id: 'FORUM_LINK_LABEL' } }).FORUM_LINK_LABEL
        );
        return `${forum} -> ${this.thread.category.name} -> ${this.thread.title}`;
    }

    @computed get gallery() {
        if (!this.deleteOn) {
            return this.photoFiles;
        }
        return [];
    }

    get isFirstMessage() {
        return this.thread.firstMessage === this;
    }

    photoSwipeItem = photoFile => photoFile.photoSwipeItem;
}

export { MessageStore };

@JSONSerializable()
class MessageStore extends ApiStore {
    threadStore: ThreadStore;
    userStore: UserStore;
    complaintStore: ComplaintStore;

    constructor(options) {
        super(options);

        const { threadStore, userStore, intl } = options;

        this.complaintStore = new ComplaintStore(options);
        this.delegate = this;
        this.threadStore = threadStore;
        this.userStore = userStore;
        this.intl = intl;
    }

    item({ thread }) {
        return new Message({
            threadStore: this.threadStore,
            userStore: this.userStore,
            delegate: this,
            thread,
            api: this.api,
            intl: this.intl,
        });
    }

    create({ thread, ...rest }) {
        const request = this.api
            .post({
                path: `forum/categories/${thread.categoryId}/threads/${thread.id}/messages`,
            })
            .send({
                thread: thread.id,
                ...rest,
            });

        return super.create({
            request,
            thread,
        });
    }

    updateContent(message, body) {
        return this.api
            .patch({
                path: `forum/categories/${message.thread.categoryId}/threads/${message.thread.id}/messages/${message.id}`,
            })
            .send({ body })
            .json()
            .then(({ json }) => this.processJSON({ json }));
    }

    delete(message, ruleId, comment) {
        const request = this.api.del({
            path: `forum/categories/${message.thread.categoryId}/threads/${message.thread.id}/messages/${message.id}`,
        });
        if (ruleId) {
            request.send({
                ruleId,
            });
        }
        if (comment) {
            request.send({
                comment,
            });
        }
        return request.json().then(({ json }) => this.processJSON({ json }));
    }

    deleteThread(message) {
        return this.api
            .del({
                path: `forum/categories/${message.thread.categoryId}/threads/${message.thread.id}/messages/${message.id}`,
            })
            .json();
    }

    loadAll(options: { thread: Thread, skipFirst?: boolean, page?: number }) {
        const { thread, page, desc, skipFirst = true } = options;
        const request = this.api.get({
            path: `forum/categories/${thread.categoryId}/threads/${thread.id}/messages`,
        });

        if (desc) {
            request.query({ ordering: '-time' });
        }

        if (skipFirst) {
            request.query({
                skip_first: true,
            });
        }

        return super.loadAll({
            page,
            request,
            thread,
        });
    }

    loadLikes(options) {
        const { likeable: message, page = 1 } = options;

        const request = this.api.get({
            path: `forum/categories/${message.thread.categoryId}/threads/${message.thread.id}/messages/${message.id}/likes`,
        });

        request.query({
            page,
        });

        return request.json().then(({ json }) => {
            return message.thread.likeStore.processPagedJSON({ json });
        });
    }

    like(options) {
        const { likeable: message } = options;

        const request = this.api.post({
            path: `forum/categories/${message.thread.categoryId}/threads/${message.thread.id}/messages/${message.id}/like`,
        });

        return request.json().then(({ json }) => this.processJSON({ json }));
    }

    unlike(options) {
        const { likeable: message } = options;

        const request = this.api.post({
            path: `forum/categories/${message.thread.categoryId}/threads/${message.thread.id}/messages/${message.id}/unlike`,
        });

        return request.json().then(({ json }) => this.processJSON({ json }));
    }

    complain({ message, comment }: { message: Message, comment?: string }) {
        return this.complaintStore.create({
            comment,
            objectId: message.id,
            appLabel: 'forum',
            model: 'message',
        });
    }
}

export { Thread };

@Likeable()
@JSONSerializable()
class Thread extends ApiItem {
    categoryStore: CategoryStore;
    messageStore: MessageStore;
    userStore: UserStore;
    photoFileStore: PhotoFileStore;
    likeStore: LikeStore;

    @observable firstMessage: Message;
    @observable lastMessage: Message;

    @observable messages: PagedResult<Message>;
    @observable related: PagedResult<Thread>;
    @observable likes;
    @observable members;

    @observable loadingRelated: boolean = false;
    @observable loadingMessages: boolean = false;

    @observable subscribed;
    @observable category: Category;

    constructor(options: { messageStore: MessageStore, photoFileStore: PhotoFileStore }) {
        super(options);

        const { messageStore, categoryStore, photoFileStore, userStore, likeStore } = options;

        this.userStore = userStore;
        this.categoryStore = categoryStore;
        this.messageStore = messageStore;
        this.photoFileStore = photoFileStore;

        this.likeStore = likeStore;
    }

    @action toggleNotifications = () => {
        if (this.subscribed) {
            return this.delegate.unsubscribe({ thread: this }).then(this.toggleNotificationsDidSucceed);
        }

        return this.delegate.subscribe({ thread: this }).then(this.toggleNotificationsDidSucceed);
    };

    @action toggleNotifications = () => {
        if (this.subscribed) {
            return this.delegate.unsubscribe({ thread: this }).then(this.toggleNotificationsDidSucceed);
        }

        return this.delegate.subscribe({ thread: this }).then(this.toggleNotificationsDidSucceed);
    };

    @action toggleNotificationsDidSucceed = response => {
        if (response.ok) {
            this.subscribed = !this.subscribed;
        }
    };

    @action loadMessagesDidSucceed = messages => {
        this.messages = messages;
        return messages;
    };

    @action loadMessagesDidFinish = () => {
        this.loadingMessages = false;
    };

    @action loadMessages(options) {
        this.loadingMessages = true;

        const { page, desc } = options || {};
        return this.messageStore
            .loadAll({
                thread: this,
                page,
                desc,
            })
            .then(this.loadMessagesDidSucceed)
            .finally(this.loadMessagesDidFinish);
    }

    @action loadRelatedDidFinish = () => {
        this.loadingRelated = false;
    };

    @action loadRelatedDidSucceed = related => {
        this.related = related;
        return related;
    };

    @action loadRelated() {
        this.loadingRelated = true;

        return this.delegate
            .loadRelated({ thread: this })
            .then(this.loadRelatedDidSucceed)
            .finally(this.loadRelatedDidFinish);
    }

    @action loadMembers() {
        this.loadingMembers = true;

        return this.delegate
            .loadMembers({ thread: this })
            .then(this.loadMembersDidSucceed)
            .finally(this.loadMembersDidFinish);
    }

    @action loadMembersDidSucceed = members => {
        this.members = members;
        return members;
    };

    @action loadMembersDidFinish = data => {
        this.loadingMembers = false;
        return data;
    };

    @action createMessage = options => {
        this.replyCount += 1;
        return this.messageStore.create({
            ...options,
            thread: this,
        });
    };

    update(category, title, body) {
        return this.delegate.update(this, category, title, body);
    }

    toggleLocked() {
        return this.delegate.toggleLocked(this);
    }

    @computed get href() {
        return this.url;
    }

    @computed get categoryId() {
        if (this.category instanceof Category) {
            return this.category.id;
        }
        return this.category;
    }

    toJSON() {
        const json = super.toJSON();

        return {
            ...json,
            category: this.categoryId,
        };
    }

    fromJSON({ json }) {
        const { firstMessage: firstMessageJSON, lastMessage: lastMessageJSON, lastMessageTime, ...rest } = json;

        this.lastMessageTime = new Date(lastMessageTime);

        super.fromJSON({ json: rest });

        if (firstMessageJSON) {
            this.firstMessage = this.messageStore.processJSON({ json: firstMessageJSON, thread: this });
        }

        if (lastMessageJSON) {
            this.lastMessage = this.messageStore.processJSON({ json: lastMessageJSON, thread: this });
        }
    }
}

export { ThreadStore };

@SlugLoadableApiStore({ path: `forum/threads` })
class ThreadStore extends ApiStore {
    categoryStore: CategoryStore;
    messageStore: MessageStore;

    @observable mostActive: PagedResult<Thread>;
    @observable mostRecent: PagedResult<Thread>;
    @observable whereIPosted: PagedResult<Thread>;

    constructor(options) {
        super(options);

        const { categoryStore, userStore, api, intl } = options;

        this.categoryStore = categoryStore;
        this.delegate = this;
        this.userStore = userStore;
        this.intl = intl;

        this.messageStore = new MessageStore({
            api,
            threadStore: this,
            userStore,
            intl,
        });
    }

    item() {
        return new Thread({
            categoryStore: this.categoryStore,
            messageStore: this.messageStore,
            delegate: this,
            userStore: this.userStore,
            photoFileStore: new PhotoFileStore({
                api: this.api.api,
            }),
            likeStore: new LikeStore({
                api: this.api.api,
                userStore: this.userStore,
            }),
        });
    }

    create({ users, category, title, body, images }) {
        const request = this.api
            .post({
                path: `forum/categories/${category.id}/threads`,
            })
            .send({
                membersIds: users.map(user => user.id),
                category: category.id,
                title,
                firstMessageBody: body,
                photofileIds: images.map(({ id }) => id),
            });

        return super.create({
            request,
        });
    }

    toggleLocked(thread) {
        const request = this.api
            .patch({
                path: `forum/categories/${thread.categoryId}/threads/${thread.id}`,
            })
            .send({
                isLocked: !thread.isLocked,
            });

        return request.json().then(result => this.processJSON(result));
    }

    update(thread, category, title, body) {
        const request = this.api
            .patch({
                path: `forum/categories/${thread.categoryId}/threads/${thread.id}`,
            })
            .send({
                category: category.id,
                title,
                firstMessageBody: body,
            });

        return request.json().then(result => this.processJSON(result));
    }

    loadAll(options: { category: Category, page?: number, asc?: boolean }) {
        const { category, page, asc } = options;

        const request = this.api.get({
            path: `forum/categories/${category.id}/threads`,
        });

        if (asc) {
            request.query({ ordering: 'last_message_time' });
        }

        request.query('fields!=first_message,last_message,subscribed,is_locked,is_sticky,liker_ids,first_message_id');

        return super.loadAll({
            page,
            request,
        });
    }

    load(options: { id: number }) {
        const { id } = options;
        const request = this.api.get({
            path: `forum/threads/${id}`,
        });

        return super.load({
            id,
            request,
        });
    }

    subscribe(options) {
        const { thread } = options;

        const request = this.api.post({
            path: `forum/categories/${thread.categoryId}/threads/${thread.id}/subscribe`,
        });
        return request.endPromise();
    }

    unsubscribe(options) {
        const { thread } = options;

        const request = this.api.del({
            path: `forum/categories/${thread.categoryId}/threads/${thread.id}/subscribe`,
        });
        return request.endPromise();
    }

    loadLikes(options) {
        const { likeable: thread, page = 1 } = options;

        const request = this.api.get({
            path: `forum/categories/${thread.categoryId}/threads/${thread.id}/messages/${thread.firstMessage.id}/likes`,
        });

        request.query({
            page,
        });

        return request.json().then(({ json }) => {
            return thread.likeStore.processPagedJSON({ json });
        });
    }

    loadMembers(options) {
        const { thread } = options;

        const request = this.api.get({
            path: `forum/categories/${thread.categoryId}/threads/${thread.id}/members`,
        });

        return request.json().then(({ json }) => {
            return this.userStore.processPagedJSON({ json });
        });
    }

    loadRelated(options) {
        const { thread } = options;

        const request = this.api.get({
            path: `forum/categories/${thread.categoryId}/threads/${thread.id}/related-threads`,
        });

        return request.json().then(({ json }) => this.processPagedJSON({ json }));
    }

    @action loadMostActive({ limit = null, page = null, pager = null } = {}) {
        const request = this.api.get({
            path: `forum/threads/most-active`,
        });

        if (limit) {
            request.query({
                pager: 'offset',
                limit,
            });
        }

        if (page) {
            request.query({
                page,
            });
        }

        if (pager) {
            request.query({
                pager,
            });
        }

        request.query('fields!=first_message,last_message,subscribed,is_locked,is_sticky,liker_ids,first_message_id');

        return super.loadAll({
            request,
        });
    }

    @action loadMostRecent({ limit = null, page = null, pager = null } = {}) {
        const request = this.api.get({
            path: `forum/threads/most-recent`,
        });

        if (limit) {
            request.query({
                pager: 'offset',
                limit,
            });
        }

        if (page) {
            request.query({
                page,
            });
        }

        if (pager) {
            request.query({
                pager,
            });
        }

        request.query(
            'fields!=first_message,first_message_username,first_message_real_name,last_message,subscribed,is_locked,is_sticky,liker_ids,first_message_id'
        );

        return super.loadAll({
            request,
        });
    }

    @action loadWhereIPostedDidSucceed = whereIPosted => {
        this.whereIPosted = whereIPosted;
        return whereIPosted;
    };

    @action loadWhereIPosted(options) {
        const { asc } = options;
        const request = this.api.get({
            path: `forum/where-i-posted`,
        });

        if (asc) {
            request.query({ ordering: 'last_message_time' });
        }

        return super
            .loadAll({
                request,
                ...options,
            })
            .then(this.loadWhereIPostedDidSucceed)
            .finally(this.loadWhereIPostedDidFinish);
    }

    @action loadSubscribedThreads(options) {
        const request = this.api.get({
            path: 'forum/subscribed-threads',
        });

        request.query('fields=id,category,title,url,subscribed');

        return super.loadAll({
            request,
            ...options,
        });
    }

    @action loadPromo({ limit = null }) {
        const request = this.api.get({
            path: 'forum/threads/promo',
        });

        if (limit) {
            request.query({
                pager: 'offset',
                limit,
            });
        }

        return super.loadAll({
            request,
            pagedType: PAGED_TYPE.OFFSET,
        });
    }
}

export { Category };

@JSONSerializable()
class Category extends ApiItem {
    threadStore: ThreadStore;

    @observable threads: PagedResult<Thread>;
    @observable loadingThreads: boolean = false;
    @observable subscribed;

    @computed get href() {
        return `/forum/category/${this.slug}/`;
    }

    constructor(options) {
        super(options);

        const { threadStore } = options;
        this.threadStore = threadStore;
    }

    @action loadThreadsDidSucceed = threads => {
        this.threads = threads;
        return threads;
    };

    @action loadThreadsDidFinish = () => {
        this.loadingThreads = false;
    };

    loadThreads(options) {
        const { page, asc } = options || {};

        this.loadingThreads = true;

        return this.threadStore
            .loadAll({ category: this, page, asc })
            .then(this.loadThreadsDidSucceed)
            .finally(this.loadThreadsDidFinish);
    }

    @action getCategorySubscription = () => {
        this.delegate.loadCategorySubscription(this).then(subscribed => {
            this.subscribed = subscribed;
        });
    };

    @action toggleNotifications = () => {
        if (this.subscribed) {
            return this.delegate.unsubscribe({ category: this }).then(this.toggleNotificationsDidSucceed);
        }

        return this.delegate.subscribe({ category: this }).then(this.toggleNotificationsDidSucceed);
    };

    @action toggleNotificationsDidSucceed = response => {
        if (response.ok) {
            this.subscribed = !this.subscribed;
        }
    };
}

export { CategoryStore };

@JSONSerializable()
@SlugLoadableApiStore({ path: 'forum/categories' })
class CategoryStore extends ApiStore<Category> {
    threadStore: ThreadStore;
    userStore: UserStore;

    @observable categories: Category[] = [];
    @observable superCategories: SuperCategory[] = [];
    @observable didLoadAll = false;

    constructor<D>(options: D) {
        super(options);

        const { api, userStore, intl } = options;

        this.delegate = this;
        this.userStore = userStore;
        this.intl = intl;

        this.threadStore = new ThreadStore({
            api,
            categoryStore: this,
            userStore,
            intl,
        });
    }

    processCategories() {
        const superCategoriesByName = {};

        this.superCategories.forEach(superCategory => {
            superCategoriesByName[superCategory.name] = superCategory;
        });

        const superCategories = new Set();

        sortBy(this.categories, ({ superCategoryColumn, superCategoryRow, orderIndex }) => [
            superCategoryColumn,
            superCategoryRow,
            orderIndex,
        ]).forEach(category => {
            const { superCategoryName, superCategoryRow, superCategoryColumn } = category;

            let superCategory;

            if (superCategoriesByName[superCategoryName]) {
                superCategory = superCategoriesByName[superCategoryName];
            } else {
                superCategory = new SuperCategory({
                    name: superCategoryName,
                    column: superCategoryColumn,
                    row: superCategoryRow,
                });
                superCategoriesByName[superCategoryName] = superCategory;
            }

            superCategories.add(superCategory);

            if (superCategory.categories.indexOf(category) == -1) {
                superCategory.categories.push(category);
            }
        });

        this.superCategories.replace(Array.from(superCategories));
    }

    processPagedJSON(options: { json: Object, jsonOptions?: JSONOptions }): Promise<PagedResult<Category>> {
        const result = super.processPagedJSON(options);
        const set = new Set(this.categories);

        result.results.forEach(item => set.add(item));
        this.categories.replace(Array.from(set));
        if (result.loadNext) {
            return result.loadNext();
        }

        this.processCategories();
        return result;
    }

    item() {
        return new Category({
            delegate: this,
            threadStore: this.threadStore,
        });
    }

    loadAll(options: { url?: string } = {}) {
        const { url } = options || {};

        const request = this.api.get({
            path: `forum/categories`,
            url,
        });

        return super
            .loadAll({
                request,
            })
            .then(result => {
                this.didLoadAll = true;
                return result;
            });
    }

    load(options: { id: number }) {
        const { id } = options;

        const request = this.api.get({
            path: `forum/categories/${id}`,
        });

        return super.load({
            id,
            request,
        });
    }

    toJSON() {
        return {
            didLoadAll: this.didLoadAll,
            data: this.data,
            categories: this.categories.map(({ id }) => id),
        };
    }

    fromJSON({ json }) {
        const { data: dataJSON, categories: categoriesJSON, didLoadAll } = json;

        this.didLoadAll = didLoadAll;

        Object.keys(dataJSON).forEach(id => {
            this.data.set(
                id,
                this.processJSON({
                    json: dataJSON[id],
                })
            );
        });

        this.categories = categoriesJSON.map(id => this.data.get(id));
        this.processCategories();
    }

    subscribe(options) {
        const { category } = options;

        const request = this.api.post({
            path: `forum/categories/${category.id}/subscribe`,
        });
        return request.endPromise();
    }

    unsubscribe(options) {
        const { category } = options;

        const request = this.api.del({
            path: `forum/categories/${category.id}/subscribe`,
        });
        return request.endPromise();
    }

    @action loadSubscribedCategories() {
        const request = this.api.get({
            path: 'forum/subscribed-categories',
        });

        request.query('fields=id,name,slug,subscribed');

        return super.loadAll({
            request,
        });
    }

    loadCategorySubscription(category) {
        const request = this.api.get({
            path: `forum/category-subscription/${category.id}`,
        });

        return request.json().then(({ json }) => {
            return json.subscribed;
        });
    }
}

export class SuperCategory {
    name: string;
    @observable categories: Category[];
    row: number;
    column: number;

    constructor(options) {
        const { name, categories = [], row, column } = options;

        this.name = name;
        this.categories = categories;
        this.row = row;
        this.column = column;
    }
}

export { Rule };

@JSONSerializable()
class Rule extends ApiItem {}

export class RuleStore extends ApiStore {
    constructor(options) {
        super(options);
        this.delegate = this;
    }

    item() {
        return new Rule({
            delegate: this,
        });
    }

    loadAll() {
        const request = this.api.get({
            path: `forum/rules`,
        });

        return super.loadAll({
            request,
            paged: false,
        });
    }
}
